import { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { getColors } from '../chartUtils';
import type { ChartConfig } from '../chartUtils';
import { MAX_MAP_MARKERS } from './constants';
import { buildColorMap } from './buildColorMap';

export default function MapView({ data, config, fullscreen = false }: { data: Record<string, unknown>[]; config: ChartConfig; fullscreen?: boolean }) {
  const latKey = config.lat_key || 'latitude';
  const lngKey = config.lng_key || 'longitude';
  const nameKey = config.name_key || 'name';
  const popupKeys = config.popup_keys;
  const markerColorKey = config.marker_color_key;
  const mapHeight = fullscreen ? '100%' : (config.height || 400);
  const colors = getColors(config);
  const defaultColor = config.color || '#ff6738';

  const validMarkers = useMemo(() => {
    const filtered = data.filter((d) => {
      const lat = d[latKey] as number;
      const lng = d[lngKey] as number;
      return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
    });
    // Cap markers to prevent browser freeze
    return filtered.length > MAX_MAP_MARKERS ? filtered.slice(0, MAX_MAP_MARKERS) : filtered;
  }, [data, latKey, lngKey]);

  const totalValid = useMemo(
    () => data.filter((d) => typeof d[latKey] === 'number' && typeof d[lngKey] === 'number').length,
    [data, latKey, lngKey],
  );

  // Build color map for marker_color_key
  const markerColorMap = useMemo(
    () => (markerColorKey ? buildColorMap(validMarkers, markerColorKey, colors) : null),
    [validMarkers, markerColorKey, colors],
  );

  const center = useMemo((): [number, number] => {
    if (validMarkers.length === 0) return [48.85, 2.35];
    const avgLat = validMarkers.reduce((s, d) => s + (d[latKey] as number), 0) / validMarkers.length;
    const avgLng = validMarkers.reduce((s, d) => s + (d[lngKey] as number), 0) / validMarkers.length;
    return [avgLat, avgLng];
  }, [validMarkers, latKey, lngKey]);

  const zoom = useMemo(() => {
    if (validMarkers.length <= 1) return 13;
    const lats = validMarkers.map((d) => d[latKey] as number);
    const lngs = validMarkers.map((d) => d[lngKey] as number);
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    if (spread > 10) return 4;
    if (spread > 5) return 6;
    if (spread > 2) return 7;
    if (spread > 0.5) return 10;
    if (spread > 0.1) return 12;
    return 13;
  }, [validMarkers, latKey, lngKey]);

  if (validMarkers.length === 0) {
    return <div className="text-muted-foreground text-sm p-4">No valid coordinates found for map ({data.length} rows, lat_key="{latKey}", lng_key="{lngKey}").</div>;
  }

  return (
    <div className="rounded-lg overflow-hidden border border-primary/10" style={{ height: mapHeight }}>
      {/* Color legend for map markers */}
      {markerColorMap && markerColorMap.size > 1 && (
        <div className="flex flex-wrap gap-3 px-3 py-1.5 bg-card/80 border-b border-primary/10 text-xs">
          {Array.from(markerColorMap.entries()).map(([val, hex]) => (
            <span key={val} className="flex items-center gap-1">
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: hex, display: 'inline-block' }} />
              <span className="text-muted-foreground">{val}</span>
            </span>
          ))}
        </div>
      )}
      {totalValid > MAX_MAP_MARKERS && (
        <div className="px-3 py-1 bg-card/60 border-b border-primary/10 text-xs text-muted-foreground">
          Showing {MAX_MAP_MARKERS} of {totalValid.toLocaleString()} markers
        </div>
      )}
      <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {validMarkers.map((d, i) => {
          const fillColor = markerColorMap && markerColorKey
            ? (markerColorMap.get(String(d[markerColorKey] ?? '')) || defaultColor)
            : defaultColor;

          return (
            <CircleMarker
              key={i}
              center={[d[latKey] as number, d[lngKey] as number]}
              radius={5}
              pathOptions={{ color: '#fff', weight: 1, fillColor, fillOpacity: 0.8 }}
            >
              <Popup>
                <strong>{(d[nameKey] as string) || `Point ${i + 1}`}</strong>
                {(popupKeys
                  ? popupKeys
                      .filter((k) => k !== nameKey && d[k] != null)
                      .map((k) => (
                        <div key={k} style={{ fontSize: '12px' }}>
                          {k}: {String(d[k])}
                        </div>
                      ))
                  : Object.entries(d)
                      .filter(([k]) => k !== latKey && k !== lngKey && k !== nameKey)
                      .slice(0, 5)
                      .map(([k, v]) => (
                        <div key={k} style={{ fontSize: '12px' }}>
                          {k}: {String(v)}
                        </div>
                      ))
                )}
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
